import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Turnstile from "react-turnstile";
import { ApplicantStage } from "../config";
import OtpInput from "react-otp-input";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";
import loginStyles from "./index.module.css";
import FeemonkLogo from "../../../../images/feemonk-logo.png";

function EligibilityLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [locationStatus, setLocationStatus] = useState<
    "pending" | "granted" | "denied"
  >("pending");

  useEffect(() => {
    if (location.state?.mobile) setMobile(location.state.mobile);
  }, []);

  // If a valid session already exists (e.g. user pressed browser back), skip login
  useEffect(() => {
    const token = sessionStorage.getItem("eligibility_auth_token");
    const stored = sessionStorage.getItem("eligibility_data");
    if (!token || !stored) return;
    try {
      const navData = JSON.parse(stored);
      if (!navData?.applicationId) return;
      axios
        .get(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${navData.applicationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        .then((res) => {
          const stage = res?.data?.data?.stage;
          if (stage === ApplicantStage.PanVerification) {
            navigate("/eligibility/income", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (
            stage === ApplicantStage.AddressDetails ||
            stage === ApplicantStage.EmploymentDetails
          ) {
            navigate("/eligibility/bank-statement", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.BankStatement) {
            navigate("/eligibility/waiting", {
              replace: true,
              state: { data: navData, nextRoute: "/eligibility/digilocker" },
            });
            return;
          }
          if (stage === ApplicantStage.AwaitingApproval) {
            navigate("/eligibility/under-review", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.Approved) {
            navigate("/eligibility/congratulations", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.SanctionTermsAccepted) {
            navigate("/eligibility/selfie", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.Digilocker) {
            navigate("/eligibility/waiting", {
              replace: true,
              state: { data: navData, fromStage10: true },
            });
            return;
          }
          if (stage === ApplicantStage.Selfie) {
            navigate("/eligibility/agreement", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.Agreement) {
            navigate("/eligibility/emandate", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.BsDone) {
            navigate("/eligibility/digilocker", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.FundSourceAssigned) {
            navigate("/eligibility/evaluating", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
          if (stage === ApplicantStage.Rejected) {
            navigate("/eligibility/rejected", {
              replace: true,
              state: { data: navData },
            });
            return;
          }
        })
        .catch(() => {});
    } catch {}
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus("denied");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        sessionStorage.setItem(
          "eligibility_location",
          JSON.stringify({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        );
        setLocationStatus("granted");
      },
      () => setLocationStatus("denied"),
    );
  };

  useEffect(() => {
    requestLocation();
    // Watch for permission change via browser address bar icon (desktop)
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" as PermissionName })
        .then((status) => {
          status.onchange = () => {
            if (status.state === "granted") requestLocation();
            else setLocationStatus("denied");
          };
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (locationStatus !== "denied") return;
    // Watch for tab becoming visible again (mobile: user went to settings and returned)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") requestLocation();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [locationStatus]);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const formatTimer = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const sendOtp = async () => {
    if (locationStatus !== "granted") {
      setError("Please enable location access to continue.");
      return;
    }
    if (mobile.length !== 10) {
      setError("Enter valid 10 digit mobile number");
      return;
    }
    if (!/^[6-9]/.test(mobile)) {
      setError("Enter a valid Indian mobile number");
      return;
    }
    if (!turnstileToken) {
      setError("Please complete the verification.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/login/create-otp`,
        { mobile, source: "eligibility", captchaToken: turnstileToken },
      );
      setTurnstileToken(null);
      setOtpSent(true);
      setTimer(54);
    } catch {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditMobile = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
    setTimer(0);
  };

  const handleResend = () => {
    if (timer > 0) return;
    setOtp("");
    setError("");
    sendOtp();
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError("Enter 6 digit OTP");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/login/verify-otp`,
        { mobile, otp },
      );
      if (res.status === 200) {
        const token = res?.data?.data;
        sessionStorage.setItem("eligibility_auth_token", token);
        sessionStorage.setItem("token", token);
        try {
          const appRes = await axios.get(
            `${process.env.REACT_APP_DASHBOARD_URL}/users/basic-info?searchField=${mobile}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const allApps: any[] = appRes?.data?.data || [];
          const excludedStatuses = [
            "Disbursed",
            "Dropped",
            "Rejected",
            "On Hold",
            "Ready to Disburse",
            "Pending Disbursement",
          ];
          const activeApps = allApps.filter(
            (a) => !excludedStatuses.includes(a?.status),
          );
          const hasDisbursed = allApps.some((a) => a?.status === "Disbursed");
          if (hasDisbursed || (allApps.length > 0 && activeApps.length === 0)) {
            navigate("/dashboard", { replace: true });
            return;
          }
          const latestApp = activeApps.reduce((prev: any, curr: any) => {
            const prevNum = parseInt(
              (prev?.applicationId || "").replace(/\D/g, ""),
              10,
            );
            const currNum = parseInt(
              (curr?.applicationId || "").replace(/\D/g, ""),
              10,
            );
            return currNum > prevNum ? curr : prev;
          }, activeApps[0]);
          if (latestApp?.applicationId) {
            sessionStorage.setItem(
              "eligibility_data",
              JSON.stringify({
                userId: latestApp.userId,
                applicationId: latestApp.applicationId,
                mobile,
              }),
            );
            try {
              const stageRes = await axios.get(
                `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${latestApp.applicationId}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const stage = stageRes?.data?.data?.stage;
              const stageStatus = stageRes?.data?.data?.status;
              const navData = {
                userId: latestApp.userId,
                applicationId: latestApp.applicationId,
                mobile,
              };
              if (stage === null && stageStatus === "In Submission") {
                const loc = JSON.parse(
                  sessionStorage.getItem("eligibility_location") || "{}",
                );
                try {
                  await axios.post(
                    `${process.env.REACT_APP_DASHBOARD_URL}/end-user/submit/qec`,
                    {
                      userId: latestApp.userId,
                      latitude: loc.latitude || 0,
                      longitude: loc.longitude || 0,
                      doneBy: "QEC",
                    },
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                } catch {}
                navigate("/eligibility/income", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              }
              if (stage === ApplicantStage.PanVerification) {
                navigate("/eligibility/income", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (
                stage === ApplicantStage.AddressDetails ||
                stage === ApplicantStage.EmploymentDetails
              ) {
                navigate("/eligibility/bank-statement", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.BankStatement) {
                navigate("/eligibility/waiting", {
                  replace: true,
                  state: {
                    data: navData,
                    nextRoute: "/eligibility/digilocker",
                  },
                });
                return;
              } else if (stage === ApplicantStage.AwaitingApproval) {
                navigate("/eligibility/under-review", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.Approved) {
                navigate("/eligibility/congratulations", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.Digilocker) {
                navigate("/eligibility/waiting", {
                  replace: true,
                  state: { data: navData, fromStage10: true },
                });
                return;
              } else if (stage === ApplicantStage.SanctionTermsAccepted) {
                navigate("/eligibility/selfie", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.Selfie) {
                navigate("/eligibility/agreement", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.Agreement) {
                navigate("/eligibility/emandate", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.BsDone) {
                navigate("/eligibility/digilocker", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.FundSourceAssigned) {
                navigate("/eligibility/evaluating", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              } else if (stage === ApplicantStage.Rejected) {
                navigate("/eligibility/rejected", {
                  replace: true,
                  state: { data: navData },
                });
                return;
              }
            } catch (e) {
              console.log("application stage error:", e);
            }
          }
        } catch (e) {
          console.log("basic-info error:", e);
        }
        navigate("/eligibility/pan", { replace: true, state: { mobile } });
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            {/* ── Above card ── */}
            {!otpSent ? (
              <div className={loginStyles.aboveCard}>
                <div className={loginStyles.lockIconBox}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      stroke="white"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M7 11V7a5 5 0 0110 0v4"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h1 className={loginStyles.heading}>
                  {/* <p className={loginStyles.headingAccent}>Sign in to   </p> */}
                  <img
                    src={FeemonkLogo}
                    alt="Feemonk Logo"
                    style={{ width: "7rem" }}
                  />
                </h1>
              </div>
            ) : (
              <div className={loginStyles.aboveCardOtp}>
                <div className={loginStyles.lockIconBox}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      stroke="white"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M7 11V7a5 5 0 0110 0v4"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h2 className={loginStyles.otpHeading}>Login</h2>
                <div className={loginStyles.otpPhoneRow}>
                  <p className={loginStyles.otpPhone}>
                    +91 {mobile}{" "}
                    <button
                      className={loginStyles.changeBtn}
                      onClick={handleEditMobile}
                    >
                      CHANGE
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                          stroke="#d32028"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.4374 22.1213 4C22.1213 4.5626 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                          stroke="#d32028"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </p>
                </div>
              </div>
            )}

            {/* ── Card ── */}
            <div
              className={styles.container}
              style={{ maxWidth: "370px", margin: "0 auto", width: "100%" }}
            >
              {!otpSent ? (
                <>
                  {/* Card inner header */}
                  <div className={loginStyles.cardInnerHeader}>
                    <div className={loginStyles.cardInnerIcon}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="5"
                          y="2"
                          width="14"
                          height="20"
                          rx="2"
                          stroke="#d32028"
                          strokeWidth="1.8"
                        />
                        <circle cx="12" cy="17" r="1" fill="#d32028" />
                        <rect
                          x="8"
                          y="6"
                          width="8"
                          height="1.5"
                          rx="0.75"
                          fill="#d32028"
                          opacity="0.5"
                        />
                        <rect
                          x="8"
                          y="9.5"
                          width="5"
                          height="1.5"
                          rx="0.75"
                          fill="#d32028"
                          opacity="0.3"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className={loginStyles.cardInnerTitle}>Login</p>
                      <p className={loginStyles.cardInnerSubtitle}>
                        Identity Verification
                      </p>
                    </div>
                  </div>

                  {/* Mobile input */}
                  <span className={loginStyles.fieldLabel}>Mobile Number</span>
                  <div className={loginStyles.phoneRow}>
                    <span className={loginStyles.countryCode}>🇮🇳 +91 ▾</span>
                    <input
                      className={loginStyles.phoneInput}
                      type="tel"
                      placeholder="000 000 0000"
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value.replace(/\D/g, ""));
                        setError("");
                      }}
                    />
                  </div>

                  {/* Location status indicator */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      margin: "0.6rem 0 0.2rem",
                    }}
                  >
                    {locationStatus === "pending" && (
                      <>
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="#aaa"
                            strokeWidth="1.8"
                            strokeDasharray="40"
                            strokeDashoffset="10"
                          />
                        </svg>
                        <span style={{ fontSize: "0.72rem", color: "#aaa" }}>
                          Requesting location access...
                        </span>
                      </>
                    )}
                    {locationStatus === "denied" && (
                      <>
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="#d32028"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M12 8V12M12 16h.01"
                            stroke="#d32028"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span style={{ fontSize: "0.72rem", color: "#d32028" }}>
                          Please enable location to continue
                        </span>
                      </>
                    )}
                    {locationStatus === "granted" && (
                      <>
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="#22c55e"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M8 12L11 15L16 9"
                            stroke="#22c55e"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span style={{ fontSize: "0.72rem", color: "#22c55e" }}>
                          Location access granted
                        </span>
                      </>
                    )}
                  </div>

                  <div style={{ margin: "0.8rem 0 0.2rem" }}>
                    <Turnstile
                      sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY!}
                      onSuccess={(token: string) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                      theme="light"
                    />
                  </div>

                  {error && <p className={loginStyles.errorText}>{error}</p>}

                  <p className={loginStyles.terms}>
                    By proceeding, you agree to our{" "}
                    <a
                      className={loginStyles.termsLink}
                      href="https://www.feemonk.com/terms-and-condition"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms and Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      className={loginStyles.termsLink}
                      href="https://www.feemonk.com/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </>
              ) : (
                <>
                  <p className={loginStyles.otpMessage}>
                    Enter the 6-digit code sent to your mobile.
                  </p>
                  <OtpInput
                    value={otp}
                    onChange={(val: string) => {
                      setOtp(val);
                      setError("");
                    }}
                    numInputs={6}
                    renderSeparator={<span />}
                    renderInput={(props: any) => <input {...props} />}
                    inputStyle={{
                      width: "2.7rem",
                      height: "3rem",
                      borderRadius: "10px",
                      border: "1.5px solid #e0e0e0",
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      textAlign: "center",
                      fontFamily: "Outfit, sans-serif",
                      color: "#1a1a2e",
                      background: "#f7f7f7",
                      outline: "none",
                    }}
                    containerStyle={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.3rem",
                      margin: "0 0 1rem",
                    }}
                  />

                  {timer > 0 && (
                    <div className={loginStyles.timerPill}>
                      <span>Request new code in</span>
                      <span className={loginStyles.timerValue}>
                        {formatTimer(timer)}
                      </span>
                    </div>
                  )}

                  <div className={loginStyles.resendRow}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M1 4v6h6M23 20v-6h-6"
                        stroke={timer > 0 ? "#bbb" : "#d32028"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15"
                        stroke={timer > 0 ? "#bbb" : "#d32028"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className={loginStyles.resendText}>
                      Didn't receive the code?{" "}
                      <span
                        className={
                          timer > 0
                            ? loginStyles.resendLinkDisabled
                            : loginStyles.resendLink
                        }
                        onClick={handleResend}
                      >
                        Resend OTP
                      </span>
                    </span>
                  </div>

                  {error && <p className={loginStyles.errorText}>{error}</p>}
                </>
              )}
            </div>

            {/* ── CTA Button ── */}
            <div
              className={loginStyles.ctaWrapper}
              style={{
                maxWidth: "370px",
                margin: "0 auto",
                width: "70%",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <button
                className={loginStyles.ctaBtn}
                onClick={otpSent ? handleVerify : sendOtp}
                disabled={loading || (!otpSent && (locationStatus !== "granted" || !turnstileToken))}
                style={
                  !otpSent && (locationStatus !== "granted" || !turnstileToken)
                    ? {
                        background: "#aaa",
                        cursor: "not-allowed",
                        opacity: 0.7,
                      }
                    : undefined
                }
              >
                {loading ? (
                  otpSent ? (
                    "Verifying..."
                  ) : (
                    "Sending..."
                  )
                ) : otpSent ? (
                  "Verify & Proceed"
                ) : (
                  <>
                    Send OTP
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12H19M13 6L19 12L13 18"
                        stroke="white"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <div style={{ display: "flex", justifyContent: "center", paddingBottom: "0.75rem" }}>
                  <button
                    onClick={() => {
                      if (!sessionStorage.getItem("eligibility_auth_token")) {
                        sessionStorage.setItem("eligibility_auth_token", "demo-skip");
                      }
                      navigate("/eligibility/pan", { state: { pan: "", dob: "", mobile: "" } });
                    }}
                    style={{
                      background: "none", border: "none", color: "rgba(255,255,255,0.25)",
                      fontSize: "0.75rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline",
                    }}
                  >
                    Skip
                  </button>
                </div>
              )}
            </div>

            {/* ── Security badges ── */}
            {/* <div className={loginStyles.securityRow} style={{ maxWidth: "370px", margin: "0 auto", width: "100%" }}>
              <div className={loginStyles.securityBadge}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.8" />
                  <path d="M9 12L11 14L15 10" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                End-to-End Encrypted
              </div>
              <div className={loginStyles.securityDot} />
              <div className={loginStyles.securityBadge}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.8" />
                  <path d="M7 11V7a5 5 0 0110 0v4" stroke="rgba(255,255,255,0.4)" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                Bank-Grade Security
              </div>
            </div> */}

            {/* ── Skip ── */}
          </div>
        </div>
        <Footer light />
      </div>
    </div>
  );
}

export default EligibilityLogin;
